<template>
  <transition name="cc-fade">
    <div
      v-if="visible"
      class="cc-overlay"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cc-title"
      aria-describedby="cc-desc"
    >
      <div class="cc-panel porsche-radius__large">
        <p v-if="!customizing">
          This website uses cookies and other tracking technologies to gather
          information about you, your device, and how you interact with this
          website to better understand how visitors use the site and to offer
          you a more personalized experience. We may share information about
          your use of this site with our security and analytics partners. By
          continuing to use this website, you agree to these practices. For more
          information, please see our
          <a
            :href="privacyUrl"
            class="cc-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy </a
          >.
        </p>
        <p>
          Select “Accept All” to allow all of these technologies and uses or
          “Required Only” to limit ones that are not essential. If you select
          “Required Only” it only applies to this website, and only to the
          browser and device you are using at the time you make the selection.
          If you clear cookies on your browser, you will need to make your
          selection again.
        </p>
        <!-- GPC notice -->
        <p v-if="gpcActive">
          <vue-fontawesome icon="fa fa-check" />
          The GPC signal is honored
        </p>

        <!-- Categories (customize view) -->
        <fieldset v-if="customizing" class="cc-categories">
          <legend class="cc-sr">Cookie categories</legend>
          <label
            v-for="cat in categoryDefs"
            :key="cat.key"
            class="cc-category"
            :class="{
              'is-locked': cat.required || (gpcActive && cat.gpcLocked)
            }"
          >
            <span class="cc-checkbox">
              <input
                type="checkbox"
                :checked="effectiveChecked(cat)"
                :disabled="cat.required || (gpcActive && cat.gpcLocked)"
                :aria-describedby="`cc-cat-desc-${cat.key}`"
                @change="onToggle(cat.key, $event.target.checked)"
              />
              <span class="cc-checkbox__visual" aria-hidden="true"></span>
            </span>
            <span class="cc-category__info">
              <span class="cc-category__head">
                <span class="cc-category__label">{{ cat.label }}</span>
                <span v-if="cat.required" class="cc-pill">Required</span>
                <span
                  v-else-if="gpcActive && cat.gpcLocked"
                  class="cc-pill cc-pill--gpc"
                >
                  Disabled by GPC
                </span>
              </span>
              <span :id="`cc-cat-desc-${cat.key}`" class="cc-category__desc">
                {{ cat.description }}
              </span>
            </span>
          </label>
        </fieldset>

        <!-- Actions -->
        <div class="cc-actions">
          <button
            type="button"
            class="cc-btn porsche-radius__small cc-btn--primary"
            @click="onAcceptAll"
          >
            Accept all
          </button>
          <button
            type="button"
            class="cc-btn porsche-radius__small cc-btn--secondary"
            @click="onRejectAll"
          >
            Required Only
          </button>
        </div>

        <p class="cc-footer">
          <template v-if="!requiresPrompt">
            <span class="cc-footer__sep" aria-hidden="true">·</span>
            <button type="button" class="cc-link-button" @click="close">
              Close
            </button>
          </template>
        </p>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

const DEFAULT_CATEGORY_DEFS = [
  {
    key: 'necessary',
    label: 'Strictly necessary',
    required: true,
    gpcLocked: false,
    description:
      'Required for the site to function (security, session, load balancing). Cannot be turned off.'
  },
  {
    key: 'functional',
    label: 'Functional',
    required: false,
    gpcLocked: false,
    description:
      'Remembers preferences such as language, theme, and UI state to enhance your experience.'
  },
  {
    key: 'analytics',
    label: 'Analytics',
    required: false,
    gpcLocked: true,
    description:
      'Helps us understand how the site is used so we can improve it. Locked off while GPC is active.'
  },
  {
    key: 'advertising',
    label: 'Advertising',
    required: false,
    gpcLocked: true,
    description:
      'Used to deliver relevant ads and measure marketing performance. Locked off while GPC is active.'
  }
];

export default {
  name: 'CookieConsent',

  props: {
    privacyUrl: { type: String, default: '/legal/privacy-policy' },
    categoryDefinitions: { type: Array, default: () => DEFAULT_CATEGORY_DEFS }
  },

  data() {
    return {
      customizing: false,
      forceOpen: false,
      selected: {
        necessary: true,
        functional: false,
        analytics: false,
        advertising: false
      }
    };
  },

  computed: {
    ...mapGetters('consent', ['gpcActive', 'requiresPrompt', 'categories']),
    visible() {
      return this.forceOpen || this.requiresPrompt;
    },
    categoryDefs() {
      return this.categoryDefinitions;
    }
  },

  watch: {
    visible(now) {
      if (now) this.selected = { ...this.categories };
    }
  },

  created() {
    this.selected = { ...this.categories };
  },

  methods: {
    ...mapActions('consent', ['acceptAll', 'rejectAll', 'updateCategories']),

    effectiveChecked(cat) {
      if (cat.required) return true;
      if (this.gpcActive && cat.gpcLocked) return false;
      return !!this.selected[cat.key];
    },

    onToggle(key, checked) {
      this.selected = { ...this.selected, [key]: checked };
    },

    open() {
      this.selected = { ...this.categories };
      this.customizing = true;
      this.forceOpen = true;
    },
    close() {
      this.customizing = false;
      this.forceOpen = false;
    },

    onAcceptAll() {
      this.acceptAll();
      this.close();
    },
    onRejectAll() {
      this.rejectAll();
      this.close();
    },
    onSave() {
      this.updateCategories(this.selected);
      this.close();
    }
  }
};
</script>

<style lang="scss" scoped>
/*
 * Porsche Design System–inspired styling.
 *
 * Tokens are exposed as CSS custom properties on .cc-panel so they can be
 * overridden globally (e.g. ":root { --cc-color-primary: var(--p-color-primary); }")
 * if your app is wired to the actual PDS theme tokens. Defaults below are
 * approximations of the PDS light theme — verify against your design tokens
 * if pixel-fidelity matters.
 */
.cc-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  background: rgba(0, 0, 0, 0.6);
  font-family: 'Porsche Next', 'Porsche Next Web', 'Arial Narrow', Arial,
    sans-serif;
  -webkit-font-smoothing: antialiased;
}

@media (min-width: 760px) {
  .cc-overlay {
    align-items: center;
    padding: 2rem;
  }
}

.cc-panel {
  /* ---- Design tokens ---- */
  --cc-color-background: #ffffff;
  --cc-color-surface: #f2f2f2;
  --cc-color-border: #c8cacb;
  --cc-color-border-strong: #0e0e12;
  --cc-color-primary: #0e0e12; /* near-black */
  --cc-color-primary-hover: #535359;
  --cc-color-text-primary: #0e0e12;
  --cc-color-text-secondary: #535359;
  --cc-color-text-inverted: #ffffff;
  --cc-color-accent: #d5001c; /* Porsche Red */
  --cc-color-accent-hover: #a80017;
  --cc-spacing-xs: 0.25rem;
  --cc-spacing-sm: 0.5rem;
  --cc-spacing-md: 1rem;
  --cc-spacing-lg: 1.5rem;
  --cc-spacing-xl: 2rem;
  --cc-spacing-2xl: 3rem;

  width: 100%;
  max-width: 760px;
  background: var(--cc-color-background);
  padding: var(--cc-spacing-xl) var(--cc-spacing-lg);
  line-height: 1.5;
  /* PDS uses square corners — no border-radius. */
}

@media (min-width: 760px) {
  .cc-panel {
    margin-left: max(22px, 10.625vw - 12px);
    margin-right: max(22px, 10.625vw - 12px);
    max-width: 1535.5px;
    min-width: 276px;
    padding: var(--cc-spacing-2xl);
    width: auto;
  }
}

/* ---------- GPC notice ---------- */
.cc-gpc {
  display: flex;
  gap: var(--cc-spacing-md);
  align-items: flex-start;
  background: var(--cc-color-surface);
  border-left: 4px solid var(--cc-color-accent);
  padding: var(--cc-spacing-md) var(--cc-spacing-lg);
  margin-bottom: var(--cc-spacing-lg);
  font-size: 0.875rem;
}

.cc-gpc__icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background: var(--cc-color-primary);
  font-weight: 400;
  font-style: italic;
  font-size: 0.875rem;
  line-height: 1;
}

.cc-gpc__body {
  flex: 1;
}
.cc-gpc strong {
  font-weight: 700;
}

/* ---------- Typography ---------- */
.cc-heading {
  margin: 0 0 var(--cc-spacing-md) 0;
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.cc-text {
  margin: 0 0 var(--cc-spacing-lg) 0;
  font-size: 1rem;
}

.cc-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

/* ---------- Categories ---------- */
.cc-categories {
  border: none;
  padding: 0;
  margin: 0 0 var(--cc-spacing-lg) 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.cc-category {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: var(--cc-spacing-sm) var(--cc-spacing-md);
  padding: var(--cc-spacing-md) 0;
  cursor: pointer;
  align-items: start;
  border-bottom: 1px solid var(--cc-color-border);
}
.cc-category:first-child {
  border-top: 1px solid var(--cc-color-border);
}
.cc-category.is-locked {
  cursor: not-allowed;
}

.cc-category__info {
  display: flex;
  flex-direction: column;
  gap: var(--cc-spacing-xs);
}
.cc-category__head {
  display: flex;
  align-items: center;
  gap: var(--cc-spacing-sm);
  flex-wrap: wrap;
}
.cc-category__label {
  font-weight: 700;
  font-size: 1rem;
}
.cc-category__desc {
  font-size: 0.875rem;
}

/* ---------- Custom square checkbox (PDS-style) ---------- */
.cc-checkbox {
  position: relative;
  width: 1.5rem;
  height: 1.5rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}
.cc-checkbox input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  margin: 0;
  cursor: inherit;
}
.cc-checkbox__visual {
  display: block;
  width: 1.5rem;
  height: 1.5rem;
  background: var(--cc-color-background);
  border: 2px solid var(--cc-color-border-strong);
  position: relative;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.cc-checkbox input:checked + .cc-checkbox__visual {
  background: var(--cc-color-primary);
  border-color: var(--cc-color-primary);
}
.cc-checkbox input:checked + .cc-checkbox__visual::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.35rem;
  height: 0.65rem;
  border-right: 2px solid var(--cc-color-text-inverted);
  border-bottom: 2px solid var(--cc-color-text-inverted);
  transform: translate(-55%, -65%) rotate(45deg);
}
.cc-checkbox input:disabled + .cc-checkbox__visual {
  border-color: var(--cc-color-border);
}
.cc-checkbox input:checked:disabled + .cc-checkbox__visual {
  background: var(--cc-color-border);
  border-color: var(--cc-color-border);
}
.cc-checkbox input:focus-visible + .cc-checkbox__visual {
  outline: 2px solid var(--cc-color-accent);
  outline-offset: 2px;
}

/* ---------- Pills ---------- */
.cc-pill {
  display: inline-block;
  font-size: 0.6875rem;
  font-weight: 700;
  background: var(--cc-color-primary);
  padding: 0.125rem 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1.4;
}
.cc-pill--gpc {
  background: var(--cc-color-accent);
}

/* ---------- Buttons ---------- */
.cc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--cc-spacing-sm);
  justify-content: flex-start;
  margin-top: var(--cc-spacing-lg);
}

.cc-btn {
  appearance: none;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 400;
  padding: 0.75rem 1.5rem;
  line-height: 1.25;
  cursor: pointer;
  border: 2px solid transparent;
  transition: background-color 120ms ease, color 120ms ease,
    border-color 120ms ease;
  letter-spacing: 0.01em;
}

.cc-btn--primary {
  background: var(--cc-color-primary);
  color: var(--cc-color-text-inverted);
  border-color: var(--cc-color-primary);
}
.cc-btn--primary:hover {
  background: var(--cc-color-primary-hover);
  border-color: var(--cc-color-primary-hover);
}

.cc-btn--secondary {
  background: var(--cc-color-background);
  color: var(--cc-color-text-primary);
  border-color: var(--cc-color-border-strong);
}
.cc-btn--secondary:hover {
  background: var(--cc-color-primary);
  color: var(--cc-color-text-inverted);
  border-color: var(--cc-color-primary);
}

.cc-btn:focus-visible {
  outline: 2px solid var(--cc-color-accent);
  outline-offset: 2px;
}

/* ---------- Footer ---------- */
.cc-footer {
  margin: var(--cc-spacing-lg) 0 0 0;
  font-size: 0.875rem;
  color: var(--cc-color-text-secondary);
  display: flex;
  gap: var(--cc-spacing-sm);
  align-items: center;
  flex-wrap: wrap;
}

.cc-footer__sep {
  color: var(--cc-color-border);
}

.cc-link {
  color: var(--cc-color-text-primary);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  font-weight: 600;
}
.cc-link:hover {
  color: var(--cc-color-accent);
}
.cc-link:focus-visible {
  outline: 2px solid var(--cc-color-accent);
  outline-offset: 2px;
}

.cc-link-button {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: var(--cc-color-text-primary);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;
  cursor: pointer;
}
.cc-link-button:hover {
  color: var(--cc-color-accent);
}
.cc-link-button:focus-visible {
  outline: 2px solid var(--cc-color-accent);
  outline-offset: 2px;
}

/* ---------- Transition ---------- */
.cc-fade-enter-active,
.cc-fade-leave-active {
  transition: opacity 220ms ease;
}
.cc-fade-enter,
.cc-fade-leave-to {
  opacity: 0;
}
</style>
